import React, { memo, useMemo } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { Slot, SlotsModuleChildProps } from '@bcraft-frontend-lib/slots'
import block from 'bem-cn'
import { AnimatePresence, motion } from 'framer-motion'

import useInfiniteVerticalScroll from 'shared/hooks/useInfiniteVerticalScroll'

import { SlotPlace } from 'modules/SlotsRoot/redefinitions'
import { IFiltersSlots } from 'modules/SlotsRoot/types/filters'
import { ISlotViewItem } from 'modules/SlotsRoot/types/interfaces/Slots/Slots'

import { selectLocaleDict } from 'features/locale/selectors'

import { EmptyFavorites } from '../EmptyFavorites/EmptyFavorites'
import { EmptyFilterResult } from '../EmptyFilterResult/EmptyFilterResult'
import { EmptySearchResult } from '../EmptySearchResult/EmptySearchResult'
import { PragmaticDGA, PragmaticDGAGame } from '../PragmaticDGA'
import SlotsIcon from '../SlotsIcon/SlotsIcon'

import './AllSlotsList.scss'

const b = block('all-slots-list')

interface IAllSlotsListProps {
  slots?: Slot[]
  slotsList?: ISlotViewItem[]
  filters: SlotsModuleChildProps<SlotPlace>['filters'] | IFiltersSlots
  modal?: boolean
  addFrame?: ((slot: Slot) => () => void) | ((slot: ISlotViewItem) => () => void)
  getGameInfo?: (gameId: string) => PragmaticDGAGame | undefined
}

export const AllSlotsList = memo(({ slots, slotsList, filters, modal, addFrame, getGameInfo }: IAllSlotsListProps) => {
  const locale = useSelector(selectLocaleDict, shallowEqual).slots
  const slotsPerRender = modal ? 12 : 28
  const games = useMemo(() => slots || slotsList || [], [slots, slotsList])

  const { selectedCategories, selectedProviders, searchString } = filters

  const { current, ref }: { current: Array<Slot | ISlotViewItem>; ref: any } = useInfiniteVerticalScroll({
    data: games,
    loadingCount: slotsPerRender,
  })

  const allSlots = useMemo(() => {
    if (searchString.length && !current.length) return <EmptySearchResult />
    if (selectedCategories.indexOf('favorite') !== -1 && !games.length) return <EmptyFavorites />
    if (!games.length) return <EmptyFilterResult />
    return (
      current?.map((slot, id) => {
        const handleAddFrame = modal ? addFrame : undefined

        const wide = modal && (id % 9 === 0 || id % 14 === 0)
        const slotId = 'gameId' in slot ? slot.gameId : String(slot.id)
        const slotProvider = 'routeProvider' in slot ? slot.routeProvider : slot.provider

        const gameInfo = getGameInfo && getGameInfo(slotId)
        if (gameInfo) {
          return (
            <PragmaticDGA
              key={`${slotId}_${slotProvider}`}
              slot={slot as ISlotViewItem}
              addFrame={handleAddFrame as ((slot: ISlotViewItem) => () => void) | undefined}
              wide={wide}
              gameInfo={gameInfo}
            />
          )
        }

        return (
          <SlotsIcon
            key={`${slotId}_${slotProvider}`}
            slot={slot}
            addFrame={handleAddFrame}
            wide={wide}
          />
        )
      }) ?? []
    )
  }, [searchString, current, selectedCategories, games, modal, addFrame, getGameInfo])

  const loading = useMemo(
    () => (
      <>
        {current.length !== games.length && games.length && games.length > slotsPerRender && (
          <div ref={ref} className={b('loading')}>
            <span>{locale.loading}</span>
          </div>
        )}
      </>
    ),
    [current, games, locale, ref, slotsPerRender],
  )

  return (
    <AnimatePresence exitBeforeEnter>
      <motion.div
        key={`${selectedCategories.join('-')}-${selectedProviders.join('-')}`}
        data-tip={`${selectedCategories.join('-') || ''}-${selectedProviders.join('-') || ''}`}
        animate={{ opacity: 1 }}
        initial={{ opacity: 0 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.1 }}>
        <div
          className={b('slots', {
            isEmpty:
              !games.length ||
              (selectedCategories.indexOf('favorite') !== -1 && !games.length) ||
              (searchString.length && !current.length),
          })}>
          {allSlots}
        </div>
        {loading}
      </motion.div>
    </AnimatePresence>
  )
})
